/**
 * Shared rich cell renderer for list/grid views (RuntimeListView + Designer canvas).
 *
 * Decoupled from `TableColumn` so both the schema-driven runtime grid and the
 * Designer's own Table-node flow can share one implementation. Keeps the exact
 * branch order (progress/percent -> chip -> currency -> formatted fallback with
 * colorField tint) plus the boolean checkmark (true → ✓, false → empty).
 */
import React from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import Typography from '@mui/material/Typography'
import CheckIcon from '@mui/icons-material/Check'
import { formatListCellValue, type ListCellAttrHint } from './formatListCellValue'
import type { ListColumnSpec } from '@/runtime/listSchema'
import { MONO_FONT } from '@/shared/typography'

export const monoSx = { fontFamily: MONO_FONT }

function fmtCurrency(n: number): string {
  return n.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 })
}

function pctBarColor(pct: number): string {
  if (pct >= 90) return '#FF3B30'
  if (pct >= 70) return '#FF9500'
  return '#34C759'
}

function isBooleanAttrType(t: string | undefined): boolean {
  const x = String(t ?? '').toLowerCase()
  return x === 'boolean' || x === 'bool' || x.includes('bool')
}

function isTruthyBool(val: unknown): boolean {
  return val === true || val === 1 || val === '1' || val === 'true'
}

/** Derives a fallback ListCellAttrHint from a column spec when no real attr metadata is available. */
export function hintFromColumn(col: ListColumnSpec): ListCellAttrHint {
  return {
    name: col.name,
    attr_type: col.attr_type
      ?? (col.format === 'date' ? 'date' : col.format === 'currency' ? 'number' : undefined),
    props: col.format === 'currency' ? { format: 'currency' } : {},
  }
}

export function renderListCell(
  col: ListColumnSpec,
  row: Record<string, unknown>,
  attrHint?: ListCellAttrHint,
  locale?: string,
): React.ReactNode {
  const hint: ListCellAttrHint = attrHint ?? hintFromColumn(col)
  // Explicit param wins; otherwise fall back to whatever `hintFor(col)` carried
  // (RuntimeListView bakes `i18n.language` onto the hint so callers that only
  // forward the hint — ListDataRow, RuntimeGroupTreeView, HierarchyTreeView —
  // resolve translatable cells correctly with no extra prop threading).
  // Gated on `translatable`: `formatListCellValue`'s `locale` param also drives
  // Intl date/number formatting for EVERY column, and that must stay exactly
  // `undefined` (visitor's real browser/OS locale) unless this column actually
  // needs locale-based translation resolution — otherwise every non-translatable
  // date/number column would silently switch to the app UI-language's Intl format.
  const effectiveLocale = hint.translatable ? (locale ?? hint.locale) : undefined
  const rawVal = row[col.name]
  // Designer ListTable often omits attr hints — still treat real booleans as bool cells.
  const asBoolean = isBooleanAttrType(hint.attr_type) || typeof rawVal === 'boolean'

  if (asBoolean) {
    return isTruthyBool(rawVal) ? (
      <CheckIcon
        fontSize="small"
        color="primary"
        data-testid="list-cell-bool-check"
        aria-label="Yes"
      />
    ) : null
  }

  if (col.progress || col.format === 'percent') {
    const raw = row[col.name]
    const val = raw == null ? null : typeof raw === 'object' ? Number((raw as { value?: number }).value) : raw
    const pct = Math.min(100, Math.max(0, Number(val) || 0))
    const bar = pctBarColor(pct)
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            flex: 1,
            height: 5,
            borderRadius: 3,
            bgcolor: 'divider',
            '& .MuiLinearProgress-bar': { bgcolor: bar, borderRadius: 3 },
          }}
        />
        <Typography sx={{ ...monoSx, fontSize: 11, color: bar, minWidth: 36 }}>{pct}%</Typography>
      </Box>
    )
  }

  if (col.chip) {
    const raw = row[col.name]
    return <Chip label={String(raw ?? '—')} size="small" sx={{ fontSize: 11, height: 22 }} />
  }

  if (col.format === 'currency') {
    const raw = row[col.name]
    const n = typeof raw === 'number' ? raw : Number(raw)
    if (!Number.isNaN(n)) return fmtCurrency(n)
  }

  const formatted = formatListCellValue(hint, row, effectiveLocale)
  const colorField = col.colorField ? row[col.colorField] : undefined
  const cellColor =
    colorField !== undefined && typeof colorField === 'number'
      ? colorField <= 30
        ? 'error.main'
        : colorField <= 60
          ? 'warning.main'
          : undefined
      : undefined

  return (
    <Typography component="span" sx={{ fontSize: 13, color: cellColor }}>
      {formatted}
    </Typography>
  )
}
